"use client";

import React, { useEffect, useState } from "react";
import { SliderContext } from "@/components/Slider/Slider.context";
import { SlideProps, SliderProps } from "@/components/Slider/Slider.interface";
import Slide from "@/components/Slider/Slide";

import styles from "./Slider.module.scss";

const Slider: React.FC<SliderProps> & { Slide: React.FC<SlideProps> } = ({
  children,
  autoplay = true,
  interval = 3000,
  showDots = true,
  showArrows = true,
  nextArrow,
  prevArrow,
  onNextClick,
  onPrevClick,
  dotsWrapperClass,
  dotClass,
  dotClassActive,
  onActive,
  slidesToShow = 1,
  slidesToScroll = 1,
  columnGap = 0,
  minSwipeDistance = 50,
  resetOnChildrenUpdate = false,
  isCircular = false,
  center = false,
  overflow = "hidden",
  defaultCurrentSlide = 0,
}) => {
  const [currentIndex, setCurrentIndex] = useState<number>(defaultCurrentSlide);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const totalSlides: number = React.Children.toArray(children).length;
  const totalDots: number = 1 + Math.ceil((totalSlides - slidesToShow) / slidesToScroll);

  useEffect(() => {
    if (totalSlides > 0 && resetOnChildrenUpdate) {
      setCurrentIndex(0);
    }
  }, [children]);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;

    if (autoplay) {
      timer = setInterval(() => {
        nextSlide();
      }, interval);
    }

    return () => {
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [currentIndex, autoplay, interval]);

  useEffect(() => {
    if (onActive && typeof onActive === "function") {
      onActive(currentIndex);
    }
  }, [currentIndex]);

  const nextSlide = (): void => {
    setCurrentIndex((prevIndex): number => {
      const nextSlide = (prevIndex + 1) % totalDots;

      if (onNextClick) {
        onNextClick(nextSlide);
      }

      return nextSlide;
    });
  };

  const prevSlide = (): void => {
    setCurrentIndex((prevIndex): number => {
      const prevSlide = (prevIndex - 1 + totalDots) % totalDots;

      if (onPrevClick) {
        onPrevClick(prevSlide);
      }

      return prevSlide;
    });
  };

  const goToSlide = (index: number): void => {
    setCurrentIndex(index);
  };

  const onTouchStart = (e: any): void => {
    setTouchEnd(null); // otherwise the swipe is fired even with usual touch events
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: any): void => setTouchEnd(e.targetTouches[0].clientX);

  const onTouchEnd = (): void => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      nextSlide();
    }

    if (isRightSwipe) {
      prevSlide();
    }
  };

  const isLastSlide: boolean = currentIndex === totalDots - 1;
  const lastSlideConst: number = (totalSlides - slidesToShow) % slidesToScroll;
  const handleLastSlide: boolean = lastSlideConst !== 0;
  const emptySlidesOnLast: number = handleLastSlide ? slidesToScroll - lastSlideConst : 0;
  const centerTranslate = center && slidesToShow < 2 ? (100 - 100 / slidesToShow) / 2 : 0;
  const translate: number =
    slidesToShow > totalSlides
      ? 0
      : currentIndex * (slidesToScroll / slidesToShow) * 100 -
        (isLastSlide && handleLastSlide && !center ? 100 * (emptySlidesOnLast / slidesToShow) : 0) -
        centerTranslate;

  return (
    <SliderContext.Provider value={{ currentIndex, columnGap, slidesToShow, isLastSlide, totalSlides, isCircular }}>
      {showArrows && totalSlides > slidesToShow && (
        <>
          {prevArrow ? (
            <span data-testid={"prev"} onClick={prevSlide}>
              {prevArrow}
            </span>
          ) : (
            <button data-testid={"prev"} type={"button"} className={styles.prev} onClick={prevSlide}>
              ❮
            </button>
          )}
        </>
      )}
      <div
        data-testid={"slider"}
        className={` ${isCircular ? styles.circularSlider : ""} ${styles.slider} ${styles[overflow]}`}
      >
        <div data-testid={"trackWrapper"} className={styles.trackWrapper}>
          <div
            data-testid={"sliderInner"}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            className={styles.sliderInner}
            style={{
              transform: isCircular ? "unset" : `translateX(${-translate}%)`,
              marginLeft: -columnGap / 2,
              marginRight: -columnGap / 2,
            }}
          >
            {!isCircular ? children : React.Children.toArray(children).reverse()}
          </div>
        </div>
        {showDots && totalSlides > slidesToShow && (
          <div data-testid={"dots"} className={`${styles.dots} ${dotsWrapperClass ? dotsWrapperClass : ""}`}>
            {Array.from({ length: totalDots }).map((_, index) => (
              <span
                key={index}
                className={`${dotClass ? dotClass : ""} ${styles.dot} ${currentIndex === index ? `${styles.active} ${dotClassActive ? dotClassActive : ""}` : ""}`}
                onClick={(): void => goToSlide(index)}
              />
            ))}
          </div>
        )}
      </div>
      {showArrows && totalSlides > slidesToShow && (
        <>
          {nextArrow ? (
            <span data-testid={"next"} onClick={nextSlide}>
              {nextArrow}
            </span>
          ) : (
            <button data-testid={"next"} className={styles.next} onClick={nextSlide}>
              ❯
            </button>
          )}
        </>
      )}
    </SliderContext.Provider>
  );
};

Slider.Slide = Slide;

export default Slider;
